import type { CurrentUser } from "@/features/auth/types"
import { canVoid } from "@/features/auth/role"
import type { InvoiceStatus } from "@/components/status-badge"

import type { BackendStatus, PaymentMode } from "./types"

/**
 * The full union of status values rows may carry on the
 * ``/invoices`` table. ``BackendStatus`` covers every status an invoice
 * can actually hold; ``StatusBadge``'s ``InvoiceStatus`` adds exactly one
 * more, ``disputed``, which is an ``Event.EventType`` rather than an
 * invoice status. We allow both here so the bulk-selection helper can be
 * called with raw row data without a cast at every call site, while still
 * catching unknown literals at compile time (typos do not silently widen
 * to ``string``).
 *
 * Since the refund statuses joined ``BackendStatus`` this union is
 * effectively ``InvoiceStatus``; collapsing the two is a follow-up
 * (fix-deposit-void-and-edit §7.3), not a change to make here.
 */
export type SelectionStatus = BackendStatus | InvoiceStatus

export type InvoiceAction =
  | "view"
  | "void"
  // Full refund of a paid one-time invoice (trigger-refund design). Offered
  // only on ``paid`` — deliberately NOT on ``partially_refunded``, which the
  // backend refuses (OQ-2a: Billdozer never finishes a refund someone started
  // in the Stripe Dashboard). The one-time-only rule (no installment or
  // deposit plan) is applied at the call site, which has the plan info a list
  // row's status alone doesn't carry — same pattern as ``amend``.
  | "refund"
  | "resend"
  | "amend"
  | "reissue"
  | "copyLink"
  // Copies the customer self-service portal link. Gate:
  // ``PORTAL_HIDDEN_STATUSES``, which carries the reasoning.
  | "copyPortalLink"
  | "downloadPdf"
  | "delete"
  // Offered only on ``created`` (issued without email): delivers the
  // customer email and moves the invoice to ``sent``. Routes to the same
  // /send/ endpoint as ``resend`` but is labelled "Send email" and gated
  // on the never-emailed state (create-invoice-without-sending-email).
  | "sendEmail"

const ACTIONABLE_STATUSES: ReadonlySet<BackendStatus> = new Set([
  "sent",
  // ``created`` (issued without emailing the customer) is Sent-shaped for
  // every rep action — voidable, amendable, copy-link / download-PDF — so
  // it joins the actionable set. Its distinct "Send email" action is added
  // separately in ``actionsForStatus``.
  "created",
  "viewed",
  "overdue",
  "payment_failed",
])

// Statuses for which the detail-page Send button is offered. ``email_failed``
// joins ``draft`` because the customer never received an email — clicking
// Send hits the same backend endpoint and either delivers or stays in
// EMAIL_FAILED. See design §5.2 / §5.5.
export const SENDABLE_STATUSES: ReadonlySet<BackendStatus> = new Set([
  "draft",
  "email_failed",
])

// The voidable, still-active statuses — exported so the deal-resolution
// classifier (``useOpenInvoicesForDeal``) and the duplicate-invoice
// prompts read the one canonical partition instead of re-listing
// statuses. Aliased to ``ACTIONABLE_STATUSES`` — this is the *standard*
// void window. The Manager-only money-collected case is layered on by
// ``voidActionFor``; reach void through that, never through this set.
export const VOIDABLE_STATUSES: ReadonlySet<BackendStatus> = ACTIONABLE_STATUSES
// The ONE-TIME amend window — the sent-but-unpaid set, the same partition
// as void (design §1). Deposits moved to the wider
// ``DEPOSIT_AMENDABLE_STATUSES`` below and installments have their own
// ``PLAN_MODIFIABLE_STATUSES``; reach all three through ``editActionFor``,
// never directly.
export const AMENDABLE_STATUSES: ReadonlySet<BackendStatus> =
  ACTIONABLE_STATUSES
// The DEPOSIT amend window. Wider than ``AMENDABLE_STATUSES`` by exactly
// ``partially_paid``: once the deposit leg settles the backend still accepts a
// balance-deadline change (deposits-deadline-edit §3.4/§5.6 —
// ``_DEADLINE_AMEND_BLOCKED_STATUSES`` is only {voided, expired, refunded}),
// and that is the primary post-send deposit edit. The form narrows itself to
// that one field via ``amendScopeFor``. Reach this through ``editActionFor``.
export const DEPOSIT_AMENDABLE_STATUSES: ReadonlySet<BackendStatus> = new Set([
  ...ACTIONABLE_STATUSES,
  "partially_paid",
])
// The DEPOSIT money-collected void window — the frontend mirror of the
// deposit row of the backend's ``_MANAGER_VOID_WIDENING`` table.
// ``partially_paid`` is "deposit settled, balance outstanding"; the two refund
// statuses are "money returned, but the balance leg is still open", which is
// the case that could not be closed at all before (BILL-178). All three are
// Manager-only and all three take the type-to-confirm step.
export const DEPOSIT_COLLECTED_VOID_STATUSES: ReadonlySet<BackendStatus> =
  new Set(["partially_paid", "refunded", "partially_refunded"])
// Refund is offered on exactly one status: a fully-paid invoice. See the
// ``"refund"`` action comment for why ``partially_refunded`` is excluded —
// it IS a ``BackendStatus`` member now, so the exclusion is a policy
// choice this set has to keep making, not something the type enforces.
export const REFUNDABLE_STATUSES: ReadonlySet<BackendStatus> = new Set([
  "paid",
])

/**
 * The single frontend definition of "Billdozer can refund this" — the
 * mirror of the backend's ``refundable_invoice`` predicate, and the one
 * place to widen when deposit / installment refunds land (trigger-refund
 * OQ-7).
 *
 * Both halves matter. The status gate alone would offer Refund on a paid
 * *plan* invoice, where the backend answers 409 and the operator gets a
 * message about Stripe partial refunds that has nothing to do with why it
 * actually failed — the "looks broken" failure the design's R4 exists to
 * avoid. ``paymentMode`` is on every list row and every detail response,
 * so no surface has to guess.
 */
export function isRefundable(
  status: BackendStatus,
  paymentMode: PaymentMode,
): boolean {
  return REFUNDABLE_STATUSES.has(status) && paymentMode === "full"
}

// The INSTALLMENTS window: statuses on which a Manager may modify a live
// plan. Frontend mirror of the backend's
// ``installments.service.MODIFIABLE_INVOICE_STATUSES``; keep the two in step.
// It adds ``partially_paid`` (a plan that has started collecting is the main
// thing a Manager reshapes) and drops ``payment_failed`` (a plan invoice
// never carries invoice-level PAYMENT_FAILED — charge failures live on the
// schedule row). ``paid`` is excluded by omission — "unless they are fully
// paid".
export const PLAN_MODIFIABLE_STATUSES: ReadonlySet<BackendStatus> = new Set([
  "created",
  "sent",
  "viewed",
  "overdue",
  "partially_paid",
])

/**
 * Which "Edit" affordance an invoice offers: ``"plan"`` (the Manager-only
 * Installment plan tab), ``"amend"`` (the discounts + deadline form) or
 * ``null`` (the backend would refuse the write). The one gate all three Edit
 * entry points read — row menu, detail buttons, the ``/amend`` route.
 *
 * Role gating is the caller's, since only a call site knows the user:
 * ``"plan"`` additionally needs ``canModifyInstallmentPlan``, ``"amend"``
 * ``canEditInvoice``.
 *
 * Mode-aware because the two windows genuinely differ, while
 * ``actionsForStatus`` stays status-only: ``partially_paid`` is editable for
 * a plan but not voidable or copy-linkable, and ``payment_failed`` is the
 * reverse.
 */
export function editActionFor(
  status: BackendStatus,
  paymentMode: PaymentMode,
): "plan" | "amend" | null {
  if (paymentMode === "installments") {
    return PLAN_MODIFIABLE_STATUSES.has(status) ? "plan" : null
  }
  if (paymentMode === "deposit") {
    return DEPOSIT_AMENDABLE_STATUSES.has(status) ? "amend" : null
  }
  return AMENDABLE_STATUSES.has(status) ? "amend" : null
}

/**
 * How much of the amend form this invoice may actually change:
 * ``"balance-deadline"`` for a deposit outside the one-time money-amend
 * window, ``"full"`` everywhere else.
 *
 * Not cosmetic. ``AmendInvoiceForm`` emits every dirty field, and the
 * backend enters its balance-deadline-only branch **only** when no
 * discount and no ``payment_deadline`` are present — so rendering a
 * discount field at ``partially_paid`` produces a body the backend
 * answers with ``409 not_amendable``. The window (``editActionFor``) and
 * the scope inside it are two different questions, so they are two
 * functions; fix-instalment-edit OQ-3 called this "its own read-only
 * treatment rather than a gate widening".
 *
 * **Derived, not hand-listed.** The restricted scope is exactly the gap
 * between the two windows: a status the deposit window admits but the
 * one-time money-amend window does not. Spelling it as
 * ``status === "partially_paid"`` would have made a later widening of
 * ``DEPOSIT_AMENDABLE_STATUSES`` silently answer ``"full"`` there, the
 * form would render the discount picker again, and the backend would
 * answer 409 — reintroducing the exact bug this function exists to
 * prevent, from a different file. Today the gap is ``partially_paid``
 * alone; that is a fact about the two sets, not a constant to maintain.
 */
export function amendScopeFor(
  status: BackendStatus,
  paymentMode: PaymentMode,
): "full" | "balance-deadline" {
  if (paymentMode !== "deposit") return "full"
  return DEPOSIT_AMENDABLE_STATUSES.has(status) &&
    !AMENDABLE_STATUSES.has(status)
    ? "balance-deadline"
    : "full"
}

/**
 * Which void affordance a row offers, or ``null`` for none.
 *
 * ``"standard"`` is today's unpaid void, offered wherever the row is
 * actionable. ``"collected"`` is the Manager-only void of an invoice that
 * has already taken money — a live installment plan mid-schedule, or a
 * deposit at ``partially_paid`` / ``refunded`` / ``partially_refunded``
 * (a refund closes the money but not the collection, so a refunded
 * deposit can still owe a balance leg). It demands the type-to-confirm
 * step in ``VoidDialog`` and is refused by the backend for anyone else.
 *
 * Deliberately NOT folded into ``ACTIONABLE_STATUSES``: that set is
 * aliased by ``VOIDABLE_STATUSES``, ``AMENDABLE_STATUSES`` and
 * ``COPY_PDF_STATUSES`` and read by ``classifyDealInvoiceStatus``, so
 * adding ``partially_paid`` there would leak amend, copy-link and PDF
 * at the same time — and would offer bulk void of money-collected rows
 * with no confirmation. Reach this through the function, never the set.
 */
export function voidActionFor(
  status: BackendStatus,
  paymentMode: PaymentMode,
): "standard" | "collected" | null {
  if (VOIDABLE_STATUSES.has(status)) return "standard"
  if (paymentMode === "installments" && status === "partially_paid") {
    return "collected"
  }
  if (paymentMode === "deposit" && DEPOSIT_COLLECTED_VOID_STATUSES.has(status)) {
    return "collected"
  }
  return null
}

// Mirrors the backend's ``invoices.service.RECEIPT_AVAILABLE_STATUSES``.
// A refunded invoice keeps its receipt: the payment still happened, and
// the document is the customer's record of it.
const RECEIPT_AVAILABLE_STATUSES: ReadonlySet<BackendStatus> = new Set([
  "paid",
  "refunded",
  "partially_refunded",
])

/**
 * Whether this invoice offers the **whole-invoice** receipt copy, or
 * nothing: ``"invoice"`` at ``paid`` / ``refunded`` / ``partially_refunded``
 * on a non-deposit invoice.
 *
 * Both halves matter.
 *
 * The refunded statuses are in because the backend already serves the
 * receipt there (``RECEIPT_AVAILABLE_STATUSES``) and the route's own
 * docstring says a refund must not break a document the customer already
 * holds — so a rep should still be able to hand it over.
 *
 * The deposit exclusion mirrors the backend: the whole-invoice branch is
 * unreachable for a deposit invoice (the route demands a ``stage``), so
 * offering the button there would hand the rep a link that 404s. A
 * deposit's receipts are its two legs, from ``depositLegActions``.
 *
 * Deliberately NOT folded into ``actionsForStatus``: its copy actions gate
 * on ``COPY_PDF_STATUSES`` (aliased to ``ACTIONABLE_STATUSES``, the
 * sent-but-unpaid window, which excludes ``paid``), so widening that set
 * would leak copy-pay-link and download-PDF onto paid rows at the same
 * time. Same escape-hatch shape as ``editActionFor`` / ``voidActionFor``;
 * reach this through the function, never through a status set.
 */
export function receiptActionFor(
  status: BackendStatus,
  paymentMode: PaymentMode,
): "invoice" | "none" {
  if (paymentMode === "deposit") return "none"
  return RECEIPT_AVAILABLE_STATUSES.has(status) ? "invoice" : "none"
}

// Resend is broader than the rest of ACTIONABLE_STATUSES because
// ``email_failed`` is also resendable from the list — the customer
// never received an email, so the FE routes the call to /send/ (the
// backend endpoint that accepts EMAIL_FAILED) while the UI label
// remains "Resend email" for operator consistency. void / copyLink /
// downloadPdf still require a delivered email, so they continue to
// gate on ACTIONABLE_STATUSES only.
const RESENDABLE_STATUSES: ReadonlySet<BackendStatus> = new Set([
  "sent",
  "viewed",
  "overdue",
  "payment_failed",
  "email_failed",
])
const COPY_PDF_STATUSES: ReadonlySet<BackendStatus> = ACTIONABLE_STATUSES

/**
 * The statuses that do NOT offer "Copy portal link" — the frontend mirror of
 * the single ``.exclude(status=DRAFT)`` in
 * ``portal.service.list_customer_payments``. A draft is not yet the
 * customer's invoice: the portal will not list it, so a link to it promises
 * a page that cannot deliver — for a first-time customer, an empty portal
 * reached through a sign-in email.
 *
 * The backend agrees rather than merely being worked around. ``request_login``
 * gates BOTH of its paths on ``_has_invoices``, which excludes DRAFT too, so a
 * draft link mails nothing even if one is reached by hand (BILL-215 §10.1).
 * This gate is the affordance half of that one rule, not a guard against a
 * permissive endpoint.
 *
 * **An exclusion, not an inclusion set.** A status added later then defaults
 * to *offered*, which is what the backend will do with it too, and the
 * harmless failure mode. The opposite shape is what caused BILL-215:
 * ``copyPortalLink`` rode along in the ``COPY_PDF_STATUSES`` branch, so it
 * inherited the payment-link window and stayed hidden on ``paid`` and
 * ``partially_paid`` as new statuses arrived. Widen this set only to hide the
 * affordance somewhere new — never to reveal it.
 */
const PORTAL_HIDDEN_STATUSES: ReadonlySet<BackendStatus> = new Set(["draft"])

const SINGLE_ONLY_ACTIONS: ReadonlySet<InvoiceAction> = new Set([
  "reissue",
  "copyLink",
  "copyPortalLink",
  "downloadPdf",
  "view",
  // "Send email" is a per-invoice action offered on the row menu / detail
  // page only — no bulk-send strip in v1.
  "sendEmail",
  // Amend opens a per-invoice modal; it has no multi-row semantic, so it
  // is stripped from the bulk-selection strip.
  "amend",
  // Refund opens a per-invoice confirm modal. No bulk refund in v1 — a
  // multi-row "return everyone's money" button is not an affordance worth
  // building.
  "refund",
])
const MUTATING_ACTIONS: ReadonlySet<InvoiceAction> = new Set([
  "void",
  "resend",
  // Send email transitions the invoice (created → sent), so it's
  // ownership-gated like the other mutators. Single-only, so already
  // stripped from multi-row selections.
  "sendEmail",
  // Amend mutates the invoice, so it's subject to the same ownership gate
  // as void/resend (a non-owner sales-rep must not see it). It's also
  // single-only, so it's already stripped from multi-row selections.
  "amend",
  // Refund moves money, so it carries the same ownership gate (canRefund
  // aliases canEditInvoice). Single-only, so already stripped from
  // multi-row selections.
  "refund",
  // Delete removes the draft, so it's ownership-gated like the others (a
  // non-owner sales-rep must not see the bulk button). It survives
  // multi-row selections — bulk-delete of DRAFT rows is offered on the
  // selection strip — but the intersection naturally gates it to
  // all-draft selections (only ``draft`` yields ``delete`` above).
  "delete",
])

export function actionsForStatus(
  status: BackendStatus,
): Set<InvoiceAction> {
  const actions = new Set<InvoiceAction>(["view"])
  // A draft is the only status the self-service DELETE endpoint accepts
  // (design §3.1 / OQ-1). The ownership gate (SR own / Manager any) is
  // applied at the call site via ``canDelete``.
  if (status === "draft") actions.add("delete")
  if (VOIDABLE_STATUSES.has(status)) actions.add("void")
  if (REFUNDABLE_STATUSES.has(status)) actions.add("refund")
  if (AMENDABLE_STATUSES.has(status)) actions.add("amend")
  if (RESENDABLE_STATUSES.has(status)) actions.add("resend")
  // A ``created`` invoice was never emailed — offer "Send email" (routes
  // to /send/) instead of the "Resend email" the delivered statuses get.
  if (status === "created") actions.add("sendEmail")
  if (status === "voided") actions.add("reissue")
  if (!PORTAL_HIDDEN_STATUSES.has(status)) actions.add("copyPortalLink")
  if (COPY_PDF_STATUSES.has(status)) {
    actions.add("copyLink")
    actions.add("downloadPdf")
  }
  return actions
}

// ---------------------------------------------------------------------------
// Deposit per-leg actions
// ---------------------------------------------------------------------------

/**
 * The actions a single deposit leg (deposit or balance) exposes in the
 * detail panel. ``copyLink`` copies that leg's pay URL, ``resend``
 * re-sends that leg's payment-link email, ``copyReceiptLink`` copies the
 * anonymous link to that leg's receipt PDF — a link the rep hands the
 * customer, so it is a copy and not a download (BILL-127 decision 4).
 */
export type DepositLegAction = "copyLink" | "resend" | "copyReceiptLink"

/** Minimal leg shape the gating helper reads. */
export interface DepositLegState {
  stage: "deposit" | "balance"
  status: string
  // When set, this leg's payment-link email has been issued at least
  // once (gates balance resend; deposit-payments spec §9).
  emailSentAt: Date | null
}

/**
 * Compute the enabled per-leg actions for a deposit leg, given the
 * invoice status. Mirrors the backend gating (deposit-payments spec §9
 * resend + §5.3 balance payability + §6.7 receipt gate):
 *
 *   - **copyLink** — always available (the rep can hand over either
 *     link), EXCEPT the balance link is suppressed until the deposit is
 *     paid (the balance leg's email_sent_at is set), per §10.3.
 *   - **resend** — deposit: while unpaid and the invoice is in a
 *     resendable status (sent/overdue/payment_failed/partially_paid).
 *     balance: only once the balance email has been issued
 *     (email_sent_at set) and the leg is unpaid.
 *   - **copyReceiptLink** — once that leg's status === "paid".
 */
const LEG_RESENDABLE_INVOICE_STATUSES: ReadonlySet<BackendStatus> = new Set([
  "sent",
  "viewed",
  "overdue",
  "payment_failed",
  "partially_paid",
])

export function depositLegActions(
  leg: DepositLegState,
  invoiceStatus: BackendStatus,
): Set<DepositLegAction> {
  const actions = new Set<DepositLegAction>()
  const isPaid = leg.status === "paid"
  const emailIssued = leg.emailSentAt !== null

  // Copy link: the balance link is gated until the deposit is paid
  // (which is exactly when the balance email is issued). The deposit
  // link is always copyable.
  if (leg.stage === "deposit" || emailIssued) {
    actions.add("copyLink")
  }

  // Resend: never for a settled leg.
  if (!isPaid) {
    if (leg.stage === "deposit") {
      if (LEG_RESENDABLE_INVOICE_STATUSES.has(invoiceStatus)) {
        actions.add("resend")
      }
    } else if (emailIssued) {
      // Balance resend only once the balance email has been issued.
      actions.add("resend")
    }
  }

  // Receipt: once the leg has settled.
  if (isPaid) actions.add("copyReceiptLink")

  return actions
}

/**
 * Map an invoice status to the duplicate-invoice resolution it implies
 * for a deal that already carries it:
 *
 *   - ``"void"`` — a voidable, still-active invoice
 *     (``sent``/``viewed``/``overdue``/``payment_failed``). The rep must
 *     void it before issuing a new one.
 *   - ``"open"`` — a draft/email_failed invoice still being worked. The
 *     rep should continue that row rather than start a second.
 *   - ``null`` — terminal/settled (``paid``/``expired``/``voided`` …). A
 *     fresh invoice is a legitimate reissue.
 *
 * ``void`` takes priority over ``open`` at the call site (design OQ-1):
 * when a deal has both, voiding the active invoice first is the
 * billing-safe path.
 */
export function classifyDealInvoiceStatus(
  status: BackendStatus,
): "void" | "open" | null {
  if (VOIDABLE_STATUSES.has(status)) return "void"
  if (SENDABLE_STATUSES.has(status)) return "open"
  return null
}

/**
 * Minimum row shape required by ``bulkActionsForSelection``. Matches
 * the ``Invoice`` row produced by the list mapper without binding to
 * the full type so the helper stays unit-testable.
 *
 * ``status`` is the union of every status the table may render
 * (``BackendStatus`` plus ``disputed``, the one badge-only state). The
 * terminal read-only statuses — ``refunded`` / ``partially_refunded`` /
 * ``disputed`` — fall through ``actionsForStatus`` to ``{view,
 * copyPortalLink}`` (both read-only; the portal link joined them in
 * BILL-215), which the multi-row strip then collapses to empty — ``view``
 * and ``copyPortalLink`` are both in ``SINGLE_ONLY_ACTIONS`` — so a
 * refunded or disputed row in the selection disables every bulk button. That is the
 * right defensive default, and it is unaffected by the refund statuses
 * having joined ``BackendStatus``: membership in the union says what an
 * invoice can be, not what may be done to it.
 */
export interface BulkSelectionEntry {
  status: SelectionStatus
  createdById?: number
}

/**
 * Compute the set of actions enabled for the toolbar above the
 * invoices table, given the currently-selected rows and the signed-in
 * user.
 *
 * Rules:
 *   - Empty selection → empty set (every toolbar button is disabled).
 *   - Per-row enabled actions are intersected: a button is enabled
 *     only when every selected row supports it.
 *   - When ``invoices.length > 1``, the single-only actions
 *     (``reissue``, ``copyLink``, ``downloadPdf``, ``view``) are
 *     stripped — they have no natural multi-row semantic per the
 *     approved design.
 *   - The mutating actions (``void``, ``resend``, ``delete``) are
 *     stripped if any selected row fails ``canVoid(user, row)`` — Sales
 *     Reps may not bulk-act on invoices owned by a different rep, and
 *     Finance may not bulk-mutate at all. ``delete`` additionally
 *     survives only an all-``draft`` selection, since ``draft`` is the
 *     one status ``actionsForStatus`` grants it.
 */
export function bulkActionsForSelection(
  invoices: readonly BulkSelectionEntry[],
  user: CurrentUser | null | undefined,
): Set<InvoiceAction> {
  if (invoices.length === 0) return new Set<InvoiceAction>()

  let intersection: Set<InvoiceAction> | null = null
  for (const row of invoices) {
    const perRow = actionsForStatus(row.status as BackendStatus)
    if (intersection === null) {
      // Defensive copy: the helper mutates ``intersection`` below;
      // ``actionsForStatus`` always allocates a fresh Set today, but
      // copying here keeps the contract intact even if a future
      // memoization layer lands on top of ``actionsForStatus``.
      intersection = new Set(perRow)
    } else {
      for (const action of intersection) {
        if (!perRow.has(action)) intersection.delete(action)
      }
    }
  }
  const result = intersection ?? new Set<InvoiceAction>()

  if (invoices.length > 1) {
    for (const action of SINGLE_ONLY_ACTIONS) result.delete(action)
  }

  const ownershipOk = invoices.every((row) =>
    canVoid(
      user,
      row.createdById !== undefined
        ? { created_by: { id: row.createdById } }
        : null,
    ),
  )
  if (!ownershipOk) {
    for (const action of MUTATING_ACTIONS) result.delete(action)
  }

  return result
}
